import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Tabs,
  Menu,
  Pagination,
} from "antd";
import "./ManageJobs.css";
import ManageJobCard from "./components/ManageJobCard";
import ManageJobsFilterDrawer from "./components/ManageJobsFilterDrawer";
import ManageJobsToolbar from "./components/ManageJobsToolbar";
import { useSelector } from "react-redux";
import FeatureGate from "../FeatureGate/FeatureGate";
import Loader from "../../components/Loading/Loader";
import { getImageByStateCode } from "../CompanyProfile/components/cloudinary";
import NoJobsIllustration from "../../assets/icons/NoJobsIllustration.svg";
import { BsSliders } from "react-icons/bs";
import history from "../../routes/history";
import { alertNotification } from "../../components/TopNotification/alertNotification";
import { getDeepCopy } from "../../shared/helpers";
import { BILLING_ELIGIBLE_SOURCE, canUseSubscriptionFeatures } from "../../shared/billing";
import _ from "lodash";
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  useLazyQuery,
} from "@apollo/client";
import { GIGS_QUERY } from "../../components/PlaceBidModal/gigsQuery";
import { HttpLink } from "apollo-link-http";
import { setContext } from "@apollo/client/link/context";
import { DEFAULT_FILTERS, PAGE_SIZE } from "./constants";
import { buildAISuggestionQuery, dedupeById, getGigStatusCount } from "./utils";

const { TabPane } = Tabs;

/* ---------------------------------------------------------------
   Main ManageJobs component
--------------------------------------------------------------- */
function ManageJobs() {

  const creditsBalance = useSelector((state) => state.plansReducer?.creditsBalance);
  const reduxProfileId = useSelector((state) => state?.profileReducer?.user?.profile?.ProfileId);

  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const subscriptionFeaturesEnabled = canUseSubscriptionFeatures(userProfile);
  const userSource = String(
    userProfile?.Source || localStorage.getItem("cognitoSource") || ""
  )
    .trim()
    .toLowerCase();
  const canUsePtmFilters =
    String(userProfile?.PvtEmpName || "").trim().toLowerCase() === "sg-ptm" &&
    userSource !== BILLING_ELIGIBLE_SOURCE;
  const isPlanExpired = subscriptionFeaturesEnabled && creditsBalance?.data?.status === 'expired';

  // localStorage wins so a child account keeps sending its master id; the profile
  // slice covers the fresh-signup window where localStorage has nothing yet.
  const resolvedProfileId =
    userProfile?.MasterAccountId || userProfile?.AccountId || reduxProfileId;
  const profileIdOnMountRef = useRef(resolvedProfileId);
  const profileIdBackfilledRef = useRef(false);

  // Apollo lazy query — no data reference kept; results handled via onCompleted callback
  const [getGigs, { loading: loadingGigs }] = useLazyQuery(GIGS_QUERY, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });

  // UI state
  const _savedFilters = (() => { try { const s = localStorage.getItem("manageJobs_state"); return s ? JSON.parse(s) : null; } catch { return null; } })();
  const [activeTab, setActiveTab] = useState(_savedFilters?.activeTab || "AllJobs");
  const [searchPublishGigs, setSearchPublishGigs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [inputSearch, setInputSearch] = useState(_savedFilters?.inputSearch || "");
  const [draftSearch, setDraftSearch] = useState(_savedFilters?.draftSearch || "");
  const [sortTypeText, setSortTypeText] = useState(_savedFilters?.sortTypeText || "Most Recent");
  const [sortParam, setSortParam] = useState(_savedFilters?.sortParam || "MOST_RECENT");
  const [currentPage, setCurrentPage] = useState(_savedFilters?.currentPage || 1);
  const [currentPageDraft, setCurrentPageDraft] = useState(_savedFilters?.currentPageDraft || 1);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(_savedFilters?.appliedFilters || { ...DEFAULT_FILTERS });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    return () => {
      try {
        localStorage.removeItem("manageJobs_state");
      } catch {}
    };
  }, []);

  // Persist filter state to localStorage on every change
  useEffect(() => {
    localStorage.setItem("manageJobs_state", JSON.stringify({ activeTab, inputSearch, draftSearch, sortTypeText, sortParam, currentPage, currentPageDraft, appliedFilters }));
  }, [activeTab, inputSearch, draftSearch, sortTypeText, sortParam, currentPage, currentPageDraft, appliedFilters]);

  // Initial load — direct calls, no effect dependencies
  useEffect(() => {
    callFetchGigs({ status: activeTab === "Draft" ? "Draft" : "All", search: activeTab === "Draft" ? draftSearch : inputSearch, page: activeTab === "Draft" ? currentPageDraft : currentPage, sort: sortParam, filters: appliedFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A fresh signup reaches this page before the profile carries an id, so the initial
  // call goes out with no MygigsProfileId. The ref keeps the id as it stood on mount,
  // so once one arrives the same call runs again — a normal load never re-fetches.
  useEffect(() => {
    if (profileIdOnMountRef.current || !resolvedProfileId || profileIdBackfilledRef.current) return;
    profileIdBackfilledRef.current = true;
    callFetchGigs({ status: activeTab === "Draft" ? "Draft" : "All", search: activeTab === "Draft" ? draftSearch : inputSearch, page: activeTab === "Draft" ? currentPageDraft : currentPage, sort: sortParam, filters: appliedFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedProfileId]);

  /* ---- shared Apollo response handler ---- */
  const onGigsCompleted = (responseData) => {
    if (responseData?.gigs?.data?.length) {
      setSearchPublishGigs(dedupeById(getDeepCopy(responseData.gigs.data)));
    } else {
      setSearchPublishGigs([]);
    }
    setTotalCount(responseData?.gigs?.aggregate?.totalCount || 0);
  };
  /* ---- helpers ---- */
  const markAsDeleted = (jobId) => {
    try {
      const copy = getDeepCopy(searchPublishGigs);
      const idx = copy.findIndex((el) => el.JobOrderId === jobId);
      if (idx >= 0) {
        copy[idx] = {
          ...copy[idx],
          JobOrderStatuses: { ...copy[idx].JobOrderStatuses, JobOrderStatusName: "Deleted" },
        };
        setSearchPublishGigs(copy);
      }
    } catch (e) {
      console.warn("markAsDeleted error", e);
    }
  };

  const handleDeleteSuccess = (response) => {
    const jobId = response?.updatedGig?.JobOrderId;

    if (jobId) {
      markAsDeleted(jobId);
    }

    alertNotification("success", "Job successfully deleted");

    if (activeTab === "Draft") {
      callFetchGigs({
        status: "Draft",
        search: draftSearch,
        page: currentPageDraft,
        sort: sortParam,
        filters: DEFAULT_FILTERS,
      });
    }
  };

  /* ---- core fetch — called directly on every event ---- */
  const callFetchGigs = ({ status = "All", search = "", page = 1, sort = sortParam, filters = appliedFilters }) => {
    getGigs({
      variables: {
        all: search || "",
        isPrivateGig: filters.isPrivateGig || false,
        MygigsProfileId: resolvedProfileId,
        _page: String(page),
        _perPage: PAGE_SIZE,
        _sort: sort,
        JobOrderStatusName: status,
        ...(filters.JobOrderTypeName ? { JobOrderTypeName: [filters.JobOrderTypeName] } : {}),
        ...(filters.Skills?.length ? { Skills: filters.Skills } : {}),
        ...(filters.cities?.length ? { cities: filters.cities } : {}),
        ...(filters.regions?.length ? { regions: filters.regions } : {}),
        ...(filters.countries?.length ? { countries: filters.countries } : {}),
        ...(filters.years_of_experience?.length ? { years_of_experience: filters.years_of_experience } : {}),
        ...(canUsePtmFilters && filters.vms?.length ? { vms: filters.vms } : {}),
        ...(canUsePtmFilters && filters.facility?.length ? { facilityIds: filters.facility } : {}),
      },
      onCompleted: onGigsCompleted,
    });
  };

  /* ---- tab change — direct API call ---- */
  const handleTabChange = (key) => {
    setActiveTab(key);
    setInputSearch("");
    setDraftSearch("");
    setCurrentPage(1);
    setCurrentPageDraft(1);
    setAppliedFilters({ ...DEFAULT_FILTERS });
    callFetchGigs({ status: key === "Draft" ? "Draft" : "All", search: "", page: 1, sort: sortParam, filters: DEFAULT_FILTERS });
  };

  /* ---- search (debounced) — direct API call ---- */
  const handleSearch = useMemo(
    () =>
      _.debounce((value) => {
        setCurrentPage(1);
        callFetchGigs({ status: appliedFilters.JobOrderStatusName || "All", search: value, page: 1, sort: sortParam, filters: appliedFilters });
      }, 600),
    [appliedFilters, sortParam] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleDraftSearch = useMemo(
    () =>
      _.debounce((value) => {
        setCurrentPageDraft(1);
        callFetchGigs({ status: "Draft", search: value, page: 1, sort: sortParam, filters: DEFAULT_FILTERS });
      }, 600),
    [sortParam] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    return () => {
      handleSearch.cancel();
      handleDraftSearch.cancel();
    };
  }, [handleSearch, handleDraftSearch]);

  /* ---- sort — direct API call ---- */
  const handleSortSelect = ({ key }) => {
    const sortMap = {
      most_recent: { label: "Most Recent", param: "MOST_RECENT" },
      upcoming_expiry: { label: "Upcoming Expiry", param: "ENDING" },
    };
    const selected = sortMap[key];
    if (!selected) return;
    setSortTypeText(selected.label);
    setSortParam(selected.param);
    callFetchGigs({ status: appliedFilters.JobOrderStatusName || "All", search: inputSearch, page: currentPage, sort: selected.param, filters: appliedFilters });
  };

  /* ---- filter apply — direct API call ---- */
  const handleFilterApply = (newFilters) => {
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    callFetchGigs({
      status: newFilters.JobOrderStatusName || "All",
      search: inputSearch,
      page: 1,
      sort: sortParam,
      filters: newFilters,
    });
  };

  /* ---- compute badge count ---- */
  const activeFilterCount =
    (appliedFilters.isPrivateGig ? 1 : 0) +
    (appliedFilters.JobOrderTypeName ? 1 : 0) +
    (appliedFilters.JobOrderStatusName !== "All" ? 1 : 0) +
    (appliedFilters.Skills?.length || 0) +
    (appliedFilters.years_of_experience?.length || 0) +
    (appliedFilters.cities?.length || 0) +
    (appliedFilters.regions?.length || 0) +
    (appliedFilters.countries?.length || 0) +
    (canUsePtmFilters ? appliedFilters.vms?.length || 0 : 0) +
    (canUsePtmFilters ? appliedFilters.facility?.length || 0 : 0);

  /* ---- sort dropdown ---- */
  const sortMenu = (
    <Menu onClick={handleSortSelect} className="mj-sort-menu">
      <Menu.Item key="most_recent">Most Recent</Menu.Item>
      <Menu.Item key="upcoming_expiry">Upcoming Expiry</Menu.Item>
    </Menu>
  );

  /* ---- card prop builder ---- */
  const buildCardProps = (item) => {
    const regionCode = item?.Regions?.Code || item?.RegionCode || item?.Location?.RegionCode;
    const cityName = item?.Cities?.CityName || item?.CityName || item?.Location?.CityName;
    const companyName = item?.CompanyName || item?.FacilityName;
    const locationLabel =
      item?.Location?.LocationWithCountry ||
      item?.Location?.Location ||
      (cityName && regionCode ? `${cityName}, ${regionCode}` : cityName || regionCode || "");

    return {
      ...item,
      jobdetails: [item],
      fromDashboard: false,
      Skills: item?.JobOrders_Skills,
      description: item.Description,
      professionCode: userProfile?.Professions?.ProfessionCode,
      screen: "My Jobs",
      VMS: item?.VMS,
      BudgetMin: Math.floor(item.BudgetMin),
      key: item._id,
      img:
        regionCode
          ? getImageByStateCode(regionCode, "state", companyName)
          : getImageByStateCode(null, "state", companyName),
      RegionCode: regionCode,
      CityName: cityName,
      id: item?.JobOrderId,
      location: locationLabel,
      title: item?.Title ?? null,
      experience: item.ExperienceLevels,
      facilityRating: item.facilityRating,
      facilityname: item.FacilityName,
      budget: item.BudgetMin,
      rates: item?.BudgetTypes?.BudgetTypeName,
      budgetType: item?.BudgetTypes?.BudgetTypeCode,
      placedbids: getGigStatusCount(item?.status, (title) => title.includes("placed")),
      matchedbids: getGigStatusCount(item?.status, (title) => title.includes("match")),
      receivedbids: getGigStatusCount(
        item?.status,
        (title) => title.includes("received") || title.includes("proposal")
      ),
      candidates: getGigStatusCount(
        item?.status,
        (title) => title.includes("invited") || title.includes("candidate")
      ),
      cardFooter: true,
      WorkplacePolicy: item.WorkplacePolicy,
      Regions: item?.Regions,
      Countries: item?.Countries,
      Location: item?.Location,
      isDeleted: item?.JobOrderStatuses?.JobOrderStatusName === "Deleted",
      indeleted: item?.JobOrderStatuses?.JobOrderStatusName === "Deleted",
      expired: item?.JobOrderStatuses?.JobOrderStatusName === "Expired",
      closed: item?.JobOrderStatuses?.JobOrderStatusName === "Closed",
      JobOrderStatusName: item?.JobOrderStatuses?.JobOrderStatusName,
      IdealContractLengthName: item?.IdealContractLength?.IdealContractLengthName,
      jobOrderType: item?.JobOrderTypes?.JobOrderTypeName,
      expiryDate: item.ExpiryDate,
      from: "ManageGigs",
      JobOrderId: item?.JobOrderId,
      rating: item?.facilityRating?.rating,
      isPrivate: item?.isPrivate,
      isFavorite: item?.IsFavorite,
      reviews: item?.facilityRating?.reviews,
      created: item?.Created,
      onAISuggestion: () => {
        const isRemote = item?.WorkplacePolicy === "fully-remote";
        const query = buildAISuggestionQuery({
          regions: isRemote ? undefined : item?.Regions,
          countries: isRemote ? undefined : item?.Countries,
          cities: isRemote ? undefined : item?.Location,
          skills: item?.JobOrders_Skills || [],
        });

        localStorage.setItem("gigDetail", JSON.stringify(item));
        history.push(query);
      },
      onExpirySuccess: () =>
        callFetchGigs({
          status: appliedFilters.JobOrderStatusName || "All",
          search: inputSearch,
          page: currentPage,
          sort: sortParam,
          filters: appliedFilters,
        }),
      onDeleteSuccess: (response) => {
        handleDeleteSuccess(response);
      },
    };
  };

  const renderCards = (items) => (
    <div className="mj-cards-wrapper">
      <Row gutter={[16, 16]}>
        {items.map((item) => (
          <Col
            key={item._id || item.JobOrderId}
            xs={24}
            sm={24}
            md={12}
            lg={8}
            xl={8}
          >
            <div className="mj-card-item">
              <ManageJobCard
                job={buildCardProps(item)}
                onExpirySuccess={() =>
                  callFetchGigs({
                    status: appliedFilters.JobOrderStatusName || "All",
                    search: inputSearch,
                    page: currentPage,
                    sort: sortParam,
                    filters: appliedFilters,
                  })
                }
                onDeleteSuccess={(response) => {
                  handleDeleteSuccess(response);
                }}
              />
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderEmpty = () => (
    <div style={{ textAlign: 'center', padding: '48px 20px' }}>
      <img src={NoJobsIllustration} alt="no-jobs" style={{ marginBottom: 24 }} />
      <div style={{ fontSize: 20, fontWeight: 700, color: '#002d40', marginBottom: 12 }}>
        Start by Posting a Job
      </div>
      <div style={{ fontSize: 14, color: '#626262', maxWidth: 420, margin: '0 auto 24px' }}>
        To place a bid, first create a job listing so we can match you with the right talent.
        Once your job is ready, you can search for suitable candidates and submit your bid.
      </div>
      <button
        onClick={() => history.push('/create-job')}
        style={{
          background: '#002d40',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '12px 28px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        + Post a Job
      </button>
    </div>
  );

  const renderFilterEmpty = () => (
    <div style={{ textAlign: 'center', padding: '48px 20px' }}>
      <img src={NoJobsIllustration} alt="no-filter-results" style={{ marginBottom: 24 }} />
      <div style={{ fontSize: 20, fontWeight: 700, color: '#002d40', marginBottom: 12 }}>
        No matches found
      </div>
      <div style={{ fontSize: 14, color: '#626262', maxWidth: 420, margin: '0 auto 24px' }}>
        We couldn’t find any jobs matching your filter result
      </div>
      <button
        onClick={() => handleFilterApply({ ...DEFAULT_FILTERS })}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          width: 291,
          height: 48,
          background: '#fff',
          color: '#002d40',
          border: '1.5px solid #002d40',
          borderRadius: 8,
          padding: '12px 24px',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <BsSliders size={16} />
        Reset Filter
      </button>
    </div>
  );

  return (
    <>
      {/* Filter Drawer */}
      <ManageJobsFilterDrawer
        visible={filterDrawerVisible}
        onClose={() => setFilterDrawerVisible(false)}
        onApply={handleFilterApply}
        appliedFilters={appliedFilters}
      />

      {/* Page */}
      <div className="mj-page" style={{ marginTop: isMobile ? "7.7rem" : "3rem" }}>
        {/* Header */}
        <div className="mj-header">
          <h1 className="mj-title">Manage Jobs</h1>
          <button
            className={isMobile ? "mj-post-btn-mobile" : "mj-post-btn"}
            onClick={() => !isPlanExpired && history.push("/create-job")}
            disabled={isPlanExpired}
            style={isPlanExpired ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
          >
            <span className="mj-post-plus" aria-hidden="true" />
            <span>Post a Job</span>
          </button>
        </div>

        {isPlanExpired ? (
          <FeatureGate
            featureName="Manage Job"
            iconType="candidate"
            description="Your free trial has ended. Choose a plan to continue managing and posting jobs."
            bottomText="Manage Job is a paid feature and was given to you for free during your trial period."
          />
        ) : (
        <div className="mj-tabs-wrapper">
          <Tabs
            className="mj-tabs"
            defaultActiveKey="AllJobs"
            onChange={handleTabChange}
            tabBarGutter={24}
          >
            {/* All Jobs Tab */}
            <TabPane tab="All Jobs" key="AllJobs">
              <ManageJobsToolbar
                onSearch={(value) => {
                  setInputSearch(value);
                  handleSearch(value);
                }}
                showFilterSort
                activeFilterCount={activeFilterCount}
                onOpenFilters={() => setFilterDrawerVisible(true)}
                sortMenu={sortMenu}
                sortTypeText={sortTypeText}
              />
              {loadingGigs ? <Loader /> : searchPublishGigs.length === 0 ? (activeFilterCount > 0 ? renderFilterEmpty() : renderEmpty()) : renderCards(searchPublishGigs)}
              {totalCount > PAGE_SIZE && (
                <Pagination
                  className="mj-pagination"
                  total={totalCount}
                  current={currentPage}
                  defaultPageSize={PAGE_SIZE}
                  onChange={(page) => {
                    setCurrentPage(page);
                    callFetchGigs({ status: appliedFilters.JobOrderStatusName || "All", search: inputSearch, page, sort: sortParam, filters: appliedFilters });
                  }}
                  showSizeChanger={false}
                  hideOnSinglePage
                />
              )}
            </TabPane>

            {/* Draft Tab */}
            <TabPane tab="Draft" key="Draft">
              <ManageJobsToolbar
                onSearch={(value) => {
                  setDraftSearch(value);
                  handleDraftSearch(value);
                }}
                showFilterSort={false}
                activeFilterCount={activeFilterCount}
                onOpenFilters={() => setFilterDrawerVisible(true)}
                sortMenu={sortMenu}
                sortTypeText={sortTypeText}
              />
              {loadingGigs ? <Loader /> : searchPublishGigs.length === 0 ? renderEmpty() : renderCards(searchPublishGigs)}
              {totalCount > PAGE_SIZE && (
                <Pagination
                  className="mj-pagination"
                  total={totalCount}
                  current={currentPageDraft}
                  defaultPageSize={PAGE_SIZE}
                  onChange={(page) => {
                    setCurrentPageDraft(page);
                    callFetchGigs({ status: "Draft", search: draftSearch, page, sort: sortParam, filters: DEFAULT_FILTERS });
                  }}
                  showSizeChanger={false}
                  hideOnSinglePage
                />
              )}
            </TabPane>
          </Tabs>
        </div>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------
   ApolloWrapper — injects auth headers into GraphQL requests
--------------------------------------------------------------- */
const ApolloWrapper = (props) => {
  const BackendApiBaseUrl = process.env.REACT_APP_API_URL;
  const authTokenValue = useSelector((state) => state.authReducer.user.authToken);
  const ImpersonationId = useSelector((state) => state.authReducer.impersonationId);
  const reduxEmail = useSelector((state) => state?.profileReducer?.user?.email);
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));

  const httpLink = new HttpLink({ uri: `${BackendApiBaseUrl}/query` });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      authorization: authTokenValue ? `${authTokenValue}` : "",
      email: userProfile?.EmailAddress || reduxEmail,
      ...(ImpersonationId ? { impersonationid: ImpersonationId } : {}),
    },
  }));

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: authLink.concat(httpLink),
    defaultOptions: { watchQuery: { nextFetchPolicy: "no-cache" } },
  });

  return (
    <ApolloProvider client={client}>
      <ManageJobs {...props} />
    </ApolloProvider>
  );
};

export default ApolloWrapper;
